/**
 * Shared time constants for `apps/web`.
 *
 * PATTERN TO FOLLOW: any cache, debounce or timeout duration used
 * in more than one place (or that represents a product decision, not a
 * local detail) lives here, not as a loose `const` inside a component.
 *
 * Why: these values were redeclared in ~12 files with two different
 * spellings of the same number (`5 * 60 * 1000` and `300_000`), so
 * changing the cache policy forced touching all of those sites and risked
 * leaving divergent values. Import from here instead of redefining.
 */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

/** Milliseconds in a minute. Base for deriving the rest of the durations. */
const MINUTE_MS = MS_PER_SECOND * SECONDS_PER_MINUTE;

/** Minutes a cached value is considered fresh (offline cache and session). */
const STALE_MINUTES = 5;

/**
 * Default `staleTime` for offline-first queries (PowerSync + TanStack).
 *
 * Data syncs locally via PowerSync, so a generous `staleTime`
 * (5 min) avoids redundant refetches/re-renders with no real risk of
 * showing stale data. It's also the `queryClient`'s global default.
 */
export const OFFLINE_CACHE_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * `staleTime` for session/authentication queries.
 *
 * Kept as its own constant (even though it currently matches the offline
 * cache value) because it's an independent decision: it must stay aligned
 * between `useSession`, the `_app` route guard and the `session-bridge`.
 */
export const SESSION_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * Wait ceiling for a `get-session` made INSIDE a route `beforeLoad`.
 *
 * P23/CC-60 · I1-bis: a `beforeLoad` that `await`s the network without a ceiling doesn't fail
 * with a 401 or a 503 (both resolve fast) — it fails when the response
 * STALLS: the router doesn't mount the route, `__root`'s
 * `<Suspense fallback={null}>` has nothing to paint and the whole app stays
 * BLANK for as long as the network takes. MEASURED in this repo against `main`
 * (`61d4e9e`), on `/login` and as a cold guest: `document.body.innerText` EMPTY at 3, 6,
 * 10, 15 and 20 s, with the control (no ceiling) painting at 10 s.
 *
 * SINGLE source for the two route guards (`_app` and `_auth`): having the value
 * in two local literals was exactly what left `_auth` without the defense
 * `_app` did have. Past the ceiling the screen paints and the late
 * response keeps syncing the machine/cache — `BootManager` redirects
 * afterwards if there turned out to be a session.
 */
export const SESSION_CHECK_TIMEOUT_MS = 1500;

/** Seconds before revoking a download's object URL (see constant). */
const OBJECT_URL_REVOKE_SECONDS = 10;

/**
 * Margin before revoking a `blob:` object URL after triggering a download.
 *
 * Must be comfortably larger than what the browser takes to consume the
 * `<a download>`'s href; revoking too early would abort the download.
 */
export const OBJECT_URL_REVOKE_DELAY_MS = OBJECT_URL_REVOKE_SECONDS * MS_PER_SECOND;
